import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRequest } from '../../hooks/useRequest';
import {
  fetchSharedChats,
  revokeSharedChat,
  SharedChatAccess,
} from '../../utils/axios/api';

function formatLocalDateTime(value: Date, language: string) {
  return new Intl.DateTimeFormat(language, {
    dateStyle: 'long',
    timeStyle: 'short',
  }).format(value);
}

function getExpiresAt(share: SharedChatAccess) {
  if (share.lifetime == null) {
    return null;
  }

  return new Date(
    new Date(share.createdAt).getTime() + share.lifetime * 1000,
  );
}

export function SharedChatsPage() {
  const { t, i18n } = useTranslation();
  const loadRequest = useRequest<SharedChatAccess[]>();
  const revokeRequest = useRequest<{ id: string }>();
  const [shares, setShares] = useState<SharedChatAccess[]>([]);
  const [revokeTarget, setRevokeTarget] = useState<SharedChatAccess | null>(
    null,
  );
  const [revokeFailed, setRevokeFailed] = useState(false);

  useEffect(() => {
    void loadRequest.fetch(() => fetchSharedChats()).then((data) => {
      if (!data) {
        return;
      }
      setShares(data);
    });
  }, [loadRequest]);

  const handleRevoke = useCallback(async () => {
    if (!revokeTarget) {
      return;
    }

    const result = await revokeRequest.fetch(() =>
      revokeSharedChat(revokeTarget.id),
    );

    if (result === null) {
      setRevokeFailed(true);
      return;
    }

    setShares((current) =>
      current.filter((share) => share.id !== revokeTarget.id),
    );
    setRevokeTarget(null);
  }, [revokeRequest, revokeTarget]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        p: 2,
      }}
    >
      <Typography variant="subtitle1">
        {t('settings.sharedChats.description')}
      </Typography>
      {loadRequest.isLoading ? (
        <CircularProgress size={24} />
      ) : loadRequest.exception && shares.length === 0 ? (
        <Typography variant="body2" color="error">
          {t(`exceptions.${loadRequest.exception}`)}
        </Typography>
      ) : shares.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('settings.sharedChats.empty')}
        </Typography>
      ) : (
        shares.map((share) => {
          const expiresAt = getExpiresAt(share);
          const isExpired = expiresAt != null && expiresAt.getTime() <= Date.now();
          const datetime = expiresAt
            ? formatLocalDateTime(expiresAt, i18n.language)
            : null;
          const availability = isExpired
            ? t('settings.sharedChats.expired', { datetime })
            : datetime
              ? t('settings.sharedChats.until', { datetime })
              : t('settings.sharedChats.unlimited');

          return (
            <Box
              key={share.id}
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                border: 1,
                borderColor: 'divider',
                borderRadius: '10px',
                px: 1.5,
                py: 1.25,
                bgcolor: 'background.paper',
              }}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body1" noWrap>
                  {share.chat.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {availability}
                </Typography>
              </Box>
              <Button
                color="error"
                variant="outlined"
                onClick={() => {
                  setRevokeFailed(false);
                  setRevokeTarget(share);
                }}
                sx={{ flexShrink: 0 }}
              >
                {t('settings.sharedChats.revoke.button')}
              </Button>
            </Box>
          );
        })
      )}
      <Dialog
        open={revokeTarget != null}
        onClose={() => {
          if (!revokeRequest.isLoading) {
            setRevokeTarget(null);
          }
        }}
      >
        <DialogTitle>{t('settings.sharedChats.revoke.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('settings.sharedChats.revoke.confirm', {
              name: revokeTarget?.chat.name ?? '',
            })}
          </DialogContentText>
          {revokeFailed && revokeRequest.exception ? (
            <Typography variant="body2" color="error" sx={{ mt: 1.5 }}>
              {t(`exceptions.${revokeRequest.exception}`)}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRevokeTarget(null)}
            disabled={revokeRequest.isLoading}
          >
            {t('settings.sharedChats.revoke.cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={revokeRequest.isLoading}
            onClick={() => {
              void handleRevoke();
            }}
          >
            {revokeRequest.isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t('settings.sharedChats.revoke.button')
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
